
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createPost } from '@/services/postService';
import { useNavigate } from 'react-router-dom';

interface LinkShareFormProps {
  user: any | null;
  inline?: boolean;
  onCancel?: () => void;
}

export const LinkShareForm = ({ user, inline = false, onCancel }: LinkShareFormProps) => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [link, setLink] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Create post mutation
  const createPostMutation = useMutation({
    mutationFn: () => {
      console.log("Sharing link:", link);
      // For link sharing, we use the link as both title and content
      return createPost(link, link, 'Weee Link 공유', user);
    },
    onSuccess: (data) => {
      toast({
        title: "링크가 공유되었습니다",
        description: "게시판으로 이동합니다."
      });
      
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      
      // Reset form
      setLink('');
      setIsSubmitting(false);
      
      if (inline) {
        // If inline, don't navigate
        if (onCancel) onCancel();
      } else {
        // Navigate to the post detail
        navigate(`/board/post/${data.id}`);
      }
    },
    onError: (error) => {
      console.error('Error submitting link:', error);
      setIsSubmitting(false);
      toast({
        variant: "destructive",
        title: "링크 공유 실패",
        description: "오류가 발생했습니다. 다시 시도해주세요."
      });
    }
  });
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!link.trim()) {
      toast({
        variant: "destructive",
        title: "링크를 입력해주세요",
        description: "공유할 링크는 필수 항목입니다."
      });
      return;
    }
    
    setIsSubmitting(true);
    createPostMutation.mutate();
  };

  if (inline) {
    return (
      <form onSubmit={handleSubmit} className="space-y-4 p-4 bg-muted/50 rounded-lg">
        <div className="flex gap-2">
          <Input
            value={link}
            onChange={(e) => setLink(e.target.value)}
            placeholder="https://"
            required
            className="flex-1"
          />
          <Button 
            type="submit" 
            disabled={isSubmitting || createPostMutation.isPending}
            size="sm"
          >
            {isSubmitting || createPostMutation.isPending ? '공유 중...' : '공유하기'}
          </Button>
          {onCancel && (
            <Button 
              type="button" 
              variant="outline" 
              onClick={onCancel}
              size="sm"
            >
              취소
            </Button>
          )}
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl mx-auto">
      <div className="space-y-2">
        <label htmlFor="link" className="block text-sm font-medium">
          공유할 링크
        </label>
        <Input
          id="link"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder="https://"
          required
          className="text-base"
        />
      </div>
      
      <div className="flex justify-end space-x-4 pt-4">
        <Button 
          type="button" 
          variant="outline" 
          onClick={() => navigate(-1)}
          disabled={isSubmitting || createPostMutation.isPending}
        >
          취소
        </Button>
        <Button 
          type="submit" 
          disabled={isSubmitting || createPostMutation.isPending}
        >
          {isSubmitting || createPostMutation.isPending ? '공유 중...' : '공유하기'}
        </Button>
      </div>
    </form>
  );
};
